import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowLeft, Sparkles, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmtPrice = (n, cur = "EUR") =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: cur,
    // Sempre 2 cifre decimali — così il prezzo NON viene arrotondato
    // (es. 9,99 € non diventa 10 €).
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);

// ---- PlanCard extracted so PayPalButtons instances don't remount on parent state changes ----
const BADGE_COLOR = {
  consigliato: { bg: "#FFD60A", fg: "#1f2937" },
  offerta:     { bg: "#FF375F", fg: "#ffffff" },
  bestseller:  { bg: "#34C759", fg: "#ffffff" },
  novita:      { bg: "#AF52DE", fg: "#ffffff" },
};

const PlanCard = ({ plan, idx, isCurrent, isMultiple, paypalConfig, onFreeSubscribe, onPaidSuccess, busy, cycle }) => {
  const p = plan;
  const anyBadged = Array.isArray(p.__all_badges) && p.__all_badges.some(Boolean);
  const highlighted = p.badge ? true : (!anyBadged && isMultiple && idx === 1);
  const badgeMeta = p.badge ? (BADGE_COLOR[p.badge] || { bg: "#FFD60A", fg: "#1f2937" }) : null;
  const monthly = Number(p.price_eur) || 0;
  const discountPct = Math.max(0, Math.min(90, Number(p.annual_discount_pct) || 0));
  const yearly = Math.round(monthly * 12 * (1 - discountPct / 100) * 100) / 100;
  const isYearly = cycle === "yearly";
  const displayed = isYearly ? yearly : monthly;
  const price = monthly; // legacy variable still used by the free-plan branch below
  const paypalReady = paypalConfig?.configured && paypalConfig?.client_id;

  return (
    <div
      data-testid={`plan-card-${p.slug}`}
      className={`relative rounded-3xl p-6 border transition-all ${
        highlighted
          ? "bg-gradient-to-br from-[#12466F] to-[#0A84FF] text-white border-transparent shadow-2xl shadow-blue-900/30 md:scale-[1.03]"
          : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg"
      }`}
    >
      {badgeMeta ? (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1 rounded-full shadow"
          style={{ background: badgeMeta.bg, color: badgeMeta.fg }}
          data-testid={`plan-banner-${p.slug}`}
        >
          {p.badge}
        </div>
      ) : (highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD60A] text-slate-900 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1 rounded-full shadow">Consigliato</div>
      ))}
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${highlighted ? "text-white/70" : "text-slate-500"}`}>{p.slug}</div>
      <div className="font-display font-black text-2xl mt-1">{p.name}</div>
      <div className="mt-4 flex items-baseline gap-1.5 flex-wrap">
        <div className="font-mono-num font-black text-4xl">{fmtPrice(displayed, p.currency)}</div>
        <div className={`text-xs ${highlighted ? "text-white/70" : "text-slate-500"}`}>/ {isYearly ? "anno" : "mese"}</div>
      </div>
      {isYearly && discountPct > 0 && monthly > 0 && (
        <div className="mt-1 flex items-center gap-2 text-[11px]">
          <span className={`line-through font-mono-num ${highlighted ? "text-white/50" : "text-slate-400"}`}>
            {fmtPrice(monthly * 12, p.currency)}
          </span>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-emerald-500 text-white">
            −{discountPct}%
          </span>
        </div>
      )}

      <ul className="mt-6 space-y-2.5 min-h-[140px]">
        {(p.features || []).slice(0, 8).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlighted ? "text-emerald-300" : "text-emerald-600"}`} />
            <span className={highlighted ? "text-white/95" : "text-slate-700"}>{f}</span>
          </li>
        ))}
      </ul>

      {p.limits && (
        <div className={`mt-4 text-[11px] ${highlighted ? "text-white/70" : "text-slate-500"}`}>
          Schede: <b>{p.limits.max_boards === -1 ? "∞" : p.limits.max_boards}</b> · Immobili: <b>{p.limits.max_properties === -1 ? "∞" : p.limits.max_properties}</b>
        </div>
      )}

      <div className="mt-6">
        {isCurrent ? (
          <Button disabled className="w-full rounded-full h-11 bg-emerald-500 text-white opacity-90" data-testid={`plan-current-${p.slug}`}>
            <Check className="w-4 h-4 mr-1.5" /> Piano attivo
          </Button>
        ) : price <= 0 ? (
          <Button
            data-testid={`plan-subscribe-${p.slug}`}
            onClick={() => onFreeSubscribe(p.slug)}
            disabled={busy === p.slug}
            className={`w-full rounded-full h-11 font-semibold ${highlighted ? "bg-white text-[#12466F] hover:bg-slate-100" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
          >
            {busy === p.slug ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Attivazione…</> : "Attiva piano gratuito"}
          </Button>
        ) : paypalReady ? (
          <div className="bg-white rounded-xl p-1" data-testid={`plan-paypal-${p.slug}`}>
            <PayPalButtons
              style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay", height: 44 }}
              createOrder={async () => {
                try {
                  const r = await api.post(`/plans/${p.slug}/paypal/create-order`, { cycle });
                  return r.data.order_id;
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore creazione ordine");
                  throw e;
                }
              }}
              onApprove={async (data) => {
                try {
                  await api.post(`/plans/${p.slug}/subscribe`, { order_id: data.orderID, cycle });
                  onPaidSuccess(p.slug);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore attivazione");
                }
              }}
              onError={() => toast.error("Errore PayPal — riprova")}
            />
          </div>
        ) : (
          <Button disabled className={`w-full rounded-full h-11 ${highlighted ? "bg-white/30 text-white" : "bg-slate-200 text-slate-500"}`}>
            Pagamenti non attivi
          </Button>
        )}
      </div>
    </div>
  );
};

const Pricing = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [paypalConfig, setPaypalConfig] = useState(null);
  // Billing cycle toggle at the top of the pricing grid. Persisted in
  // localStorage so returning users see their previous choice.
  const [cycle, setCycle] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem("pricing.cycle")) || "monthly",
  );
  useEffect(() => { try { localStorage.setItem("pricing.cycle", cycle); } catch {} }, [cycle]);

  useEffect(() => {
    (async () => {
      try {
        const [pr, pp] = await Promise.all([
          api.get("/plans"),
          user ? api.get("/paypal/config").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        setPlans(pr.data || []);
        setPaypalConfig(pp.data);
      } catch { toast.error("Errore nel caricamento dei piani"); }
      finally { setLoading(false); }
    })();
  }, [user]);

  const refreshUser = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {}
  };

  const onFreeSubscribe = async (slug) => {
    if (!user) { navigate("/"); return; }
    setBusy(slug);
    try {
      await api.post(`/plans/${slug}/subscribe`);
      await refreshUser();
      toast.success("Piano attivato! Benvenuto tra gli abbonati");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'attivazione");
    } finally { setBusy(""); }
  };

  const onPaidSuccess = async (slug) => {
    await refreshUser();
    toast.success(`Piano "${slug}" attivato — grazie!`);
    navigate("/account");
  };

  const paypalOptions = useMemo(() => ({
    "client-id": paypalConfig?.client_id,
    currency: paypalConfig?.currency || "EUR",
    intent: "capture",
  }), [paypalConfig]);

  const content = (
    <div className="max-w-6xl mx-auto px-6 py-10 lg:py-14">
      <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-8" data-testid="pricing-back">
        <ArrowLeft className="w-4 h-4" /> Torna indietro
      </Link>

      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold bg-slate-100 rounded-full px-3 py-1 mb-4">
          <Sparkles className="w-3 h-3" /> Piani di abbonamento
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none text-slate-900">
          Scegli il piano <span style={{ color: "#12466F" }}>giusto</span> per te
        </h1>
        <p className="mt-4 text-slate-600 text-lg">Sblocca l'importazione da URL, l'analisi con AI e schede illimitate.</p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 rounded-full px-3 py-1">
          <ShieldCheck className="w-3 h-3" /> Pagamenti processati in sicurezza da PayPal
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <div
          className="inline-flex rounded-full bg-slate-100 p-1 shadow-inner"
          role="tablist"
          data-testid="pricing-cycle-toggle"
        >
          <button
            role="tab"
            aria-selected={cycle === "monthly"}
            onClick={() => setCycle("monthly")}
            className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${cycle === "monthly" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
            data-testid="pricing-cycle-monthly"
          >
            Mensile
          </button>
          <button
            role="tab"
            aria-selected={cycle === "yearly"}
            onClick={() => setCycle("yearly")}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${cycle === "yearly" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
            data-testid="pricing-cycle-yearly"
          >
            Annuale
            {(() => {
              const maxDisc = Math.max(0, ...plans.map((p) => Number(p.annual_discount_pct) || 0));
              return maxDisc > 0 ? (
                <span className="ml-1 inline-flex items-center rounded-full bg-emerald-500 text-white px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold">
                  −fino a {maxDisc}%
                </span>
              ) : null;
            })()}
          </button>
        </div>
      </div>

      {loading && <div className="mt-12 text-center text-slate-500">Caricamento piani…</div>}
      {!loading && plans.length === 0 && (
        <div className="mt-12 text-center text-slate-500 max-w-lg mx-auto">
          Nessun piano ancora pubblicato. L'admin li configurerà a breve in Settings → Piani.
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-grid">
        {plans.map((p, idx) => (
          <PlanCard
            key={p.slug}
            plan={p}
            idx={idx}
            isMultiple={plans.length > 1}
            isCurrent={user && user.plan_key === p.slug}
            paypalConfig={paypalConfig}
            onFreeSubscribe={onFreeSubscribe}
            onPaidSuccess={onPaidSuccess}
            busy={busy}
            cycle={cycle}
          />
        ))}
      </div>

      <p className="mt-10 text-center text-xs text-slate-500">
        Puoi cambiare o annullare il piano in qualsiasi momento dal tuo profilo.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {paypalConfig?.client_id ? (
        <PayPalScriptProvider options={paypalOptions}>{content}</PayPalScriptProvider>
      ) : content}
    </div>
  );
};

export default Pricing;
